import React, { useState } from "react";

// material-ui
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Tooltip,
  CircularProgress,
  FormControlLabel,
  useTheme,
  Autocomplete,
} from "@mui/material";

// third-party
import _ from "lodash";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// project imports
import CircularWithPath from "../../../../../../components/@extended/progress/CircularWithPath";
import { openSnackbar } from "../../../../../../api/snackbar";

// assets
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

// types
import { SnackbarProps } from "../../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import { USERAXIOS } from "../../../../../../api/userAxios";
import ReusableModal from "../../../../../../components/ReusableComponent/ReusableModal";

// hooks
import {
  ResumeSubscription,
  ResumeSubscriptionFormData,
} from "../../../../../../types/orders-managment/subscriptions";
import { useOrderItems } from "../../../../../../hooks/query-hooks/useOrderItems";
import { useResumePackages } from "../../../../../../hooks/query-hooks/useResumePackages";
import { useUsers } from "../../../../../../hooks/query-hooks/useUsers";
import { Resume } from "../../../../../../types/resumes-managment/resume";

const getInitialValues = (
  customer: ResumeSubscription | null
): ResumeSubscriptionFormData => {
  if (customer) {
    return {
      user_id: customer.user_id?.toString() || "",
      order_item_id: customer.order_item_id?.toString() || "",
      status: customer.status || false,
      resume_package_id: customer.resume_package_id?.toString() || "",
      amount_resumes: customer.amount_resumes?.toString() || "",
      used_resumes: customer.used_resumes?.toString() || "",
    };
  } else {
    return {
      user_id: "",
      resume_package_id: "",
      amount_resumes: "",
      status: false,
      order_item_id: "",
      used_resumes: "",
    };
  }
};

// ==============================|| RESUME SUBSCRIPTION ADD / EDIT - FORM ||============================== //

const AddEditResumeSubscription = ({
  open,
  customer,
  id,
  loading,
  fetchData,
  setLoading,
  handleClose,
  setResult,
}: {
  open: boolean;
  loading: boolean;
  id: number;
  setLoading: any;
  fetchData: any;
  customer: ResumeSubscription | null;
  handleClose: () => void;
  setResult: any;
}) => {
  const intl = useIntl();
  const theme = useTheme();

  const [serverErrors, setServerErrors] = useState<any>({});

  // const [resumeSearchTerm, setResumeSearchTerm] = useState("");

  // const debouncedResumeSearchTerm = useDebounce(resumeSearchTerm, 500);
  // const [selectedResume, setSelectedResume] = useState<Resume | null>(null);

  // const {
  //   items: resumes,
  //   loading: resumesLoading,
  //   loadMore: loadMoreResumes,
  //   hasMore: hasMoreResumes,
  // } = useResumesForAutocomplete(20, debouncedResumeSearchTerm);

  const {
    data: orders = [],
    isLoading: ordersLoading,
    error: ordersError,
  } = useOrderItems();

  const {
    data: packages = [],
    isLoading: packagesLoading,
    error: packagesError,
  } = useResumePackages();

  const {
    data: companies = [],
    isLoading: companiesLoading,
    error: companiesError,
  } = useUsers("COMPANY");

  const CustomerSchema = Yup.object().shape({
    user_id: Yup.string().required("Company Name is required"),
    resume_package_id: Yup.string().required("Resumes Package is required"),
    amount_resumes: Yup.number().required("Amount Resumes is required"),
    status: Yup.boolean().required("Status is required"),
    order_item_id: Yup.string().required("Resume Order is required"),
    used_resumes: Yup.number().required("Used Resumes is required"),
  });

  const formik = useFormik({
    initialValues: getInitialValues(customer!),
    validationSchema: CustomerSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setServerErrors({});

      try {
        const subscriptionData = {
          user_id: parseInt(values.user_id),
          resume_package_id: parseInt(values.resume_package_id),
          amount_resumes: parseInt(values.amount_resumes),
          status: values.status,
          order_item_id: parseInt(values.order_item_id),
          used_resumes: parseInt(values.used_resumes),
        };

        if (customer) {
          try {
            const response = await USERAXIOS.post(
              `/resume_subscriptions/${customer.id}`,
              subscriptionData
            );
            const updatedSubscription = response.data.data;

            if (updatedSubscription) {
              setResult((prev: any[]) =>
                prev.map((sub) =>
                  sub.id === updatedSubscription.id ? updatedSubscription : sub
                )
              );

              openSnackbar({
                open: true,
                message: "Resume Subscription updated successfully",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: { color: "success" },
              } as SnackbarProps);
            }
            handleClose();
          } catch (error: any) {
            const fieldErrors =
              error?.response?.data?.errors || error?.response?.data?.message;
            if (fieldErrors && typeof fieldErrors === "object") {
              setServerErrors(fieldErrors);
            }

            openSnackbar({
              open: true,
              message:
                typeof error?.response?.data?.message === "string"
                  ? error.response.data.message
                  : "Failed to update resume subscription",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "error" },
            } as SnackbarProps);
          }
        } else {
          try {
            const response = await USERAXIOS.post(
              `/resume_subscriptions`,
              subscriptionData
            );
            const newSubscription = response.data.data;

            if (newSubscription) {
              setResult((prev: any[]) => [...prev, newSubscription]);

              openSnackbar({
                open: true,
                message: "Resume Subscription added successfully",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: { color: "success" },
              } as SnackbarProps);
            }
            handleClose();
          } catch (error: any) {
            const fieldErrors =
              error?.response?.data?.errors || error?.response?.data?.message;
            if (fieldErrors && typeof fieldErrors === "object") {
              setServerErrors(fieldErrors);
            }

            openSnackbar({
              open: true,
              message:
                typeof error?.response?.data?.message === "string"
                  ? error.response.data.message
                  : "Failed to create resume subscription",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "error" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
      } catch (error: any) {
        console.error("Error submitting form:", error);
        openSnackbar({
          open: true,
          message:
            typeof error?.response?.data?.message === "string"
              ? error.response.data.message
              : "An error occurred",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: { color: "error" },
        } as SnackbarProps);
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  if (loading) {
    return (
      <Box sx={{ p: 5 }}>
        <Stack direction="row" justifyContent="center">
          <CircularWithPath />
        </Stack>
      </Box>
    );
  }

  return (
    <ReusableModal open={open} handleClose={handleClose}>
      <FormikProvider value={formik}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle
              color="primary"
              style={{
                textAlign: "center",
                fontSize: "22px",
                fontWeight: "bold",
                marginBottom: "10px",
              }}
            >
              {customer ? (
                <FormattedMessage id="Edit Resume Subscription" />
              ) : (
                <FormattedMessage id="Add Resume Subscription" />
              )}
            </DialogTitle>
            <Divider />

            <DialogContent sx={{ p: 2.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-order_item_id">
                      <FormattedMessage id="Order" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Select resume order"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <Select
                      id="order-item-id"
                      displayEmpty
                      {...getFieldProps("order_item_id")}
                      onChange={(event: SelectChangeEvent<string>) => {
                        setFieldValue("order_item_id", event.target.value);
                      }}
                      error={Boolean(
                        touched?.order_item_id && errors?.order_item_id
                      )}
                      disabled={ordersLoading || !!customer}
                    >
                      {ordersLoading ? (
                        <MenuItem disabled>Loading orders...</MenuItem>
                      ) : ordersError ? (
                        <MenuItem disabled>Error loading orders</MenuItem>
                      ) : orders.length === 0 ? (
                        <MenuItem disabled>No orders available</MenuItem>
                      ) : (
                        orders.map((order: any) => (
                          <MenuItem key={order.id} value={order.id.toString()}>
                            {order.order_number || order.id}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {touched?.order_item_id && errors?.order_item_id && (
                      <FormHelperText error>
                        {errors?.order_item_id}
                      </FormHelperText>
                    )}
                    {serverErrors?.order_item_id && (
                      <FormHelperText error>
                        {serverErrors?.order_item_id[0]}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-user_id">
                      <FormattedMessage id="Company Name" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Select company"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <Select
                      id="user-id"
                      displayEmpty
                      {...getFieldProps("user_id")}
                      onChange={(event: SelectChangeEvent<string>) => {
                        const selectedId = event.target.value;
                        const selectedCompany = companies.find(
                          (company) => company.id.toString() === selectedId
                        );
                        setFieldValue("user_id", selectedId);
                        setFieldValue(
                          "company_name",
                          selectedCompany?.user_name || ""
                        );
                      }}
                      error={Boolean(touched.user_id && errors.user_id)}
                      disabled={companiesLoading || !!customer}
                    >
                      {companiesLoading ? (
                        <MenuItem disabled>Loading companies...</MenuItem>
                      ) : companiesError ? (
                        <MenuItem disabled>Error loading companies</MenuItem>
                      ) : companies.length === 0 ? (
                        <MenuItem disabled>No companies available</MenuItem>
                      ) : (
                        companies.map((company: any) => (
                          <MenuItem
                            key={company.id}
                            value={company.id.toString()}
                          >
                            {company.user_name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {touched.user_id && errors.user_id && (
                      <FormHelperText error>{errors.user_id}</FormHelperText>
                    )}
                    {serverErrors.user_id && (
                      <FormHelperText error>
                        {serverErrors.user_id}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-resume_package_id">
                      <FormattedMessage id="Package ID" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Select resume package"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <Select
                      id="customer-resume_package_id"
                      displayEmpty
                      {...getFieldProps("resume_package_id")}
                      onChange={(event: SelectChangeEvent<string>) => {
                        setFieldValue("resume_package_id", event.target.value);
                      }}
                      error={Boolean(
                        touched.resume_package_id && errors.resume_package_id
                      )}
                      disabled={!packages || packages.length === 0}
                    >
                      {!packages || packages.length === 0 ? (
                        <MenuItem disabled>No packages available</MenuItem>
                      ) : (
                        packages.map((pkg: any) => (
                          <MenuItem key={pkg.id} value={pkg.id.toString()}>
                            {pkg.id}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {touched.resume_package_id && errors.resume_package_id && (
                      <FormHelperText error>
                        {errors.resume_package_id}
                      </FormHelperText>
                    )}
                    {serverErrors.resume_package_id && (
                      <FormHelperText error>
                        {serverErrors.resume_package_id}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-used_resumes">
                      <FormattedMessage id="Used Resumes" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Enter number of used resumes"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="customer-used_resumes"
                      placeholder="Enter Used Resumes"
                      type="number"
                      {...getFieldProps("used_resumes")}
                      error={Boolean(
                        touched.used_resumes && errors.used_resumes
                      )}
                      helperText={touched.used_resumes && errors.used_resumes}
                    />
                    {serverErrors.used_resumes && (
                      <FormHelperText error>
                        {serverErrors.used_resumes}
                      </FormHelperText>
                    )}
                    {/* <Autocomplete
                      options={resumes}
                      getOptionLabel={(option) =>
                        option.objectives || option.id?.toString() || ""
                      }
                      value={selectedResume}
                      onChange={(_, newValue) => setSelectedResume(newValue)}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === "input") {
                          setResumeSearchTerm(newInputValue);
                        }
                      }}
                      loading={resumesLoading}
                      filterOptions={(x) => x}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={<FormattedMessage id="Select resume" />}
                          InputProps={{
                            ...params.InputProps,
                          }}
                        />
                      )}
                      ListboxProps={{
                        onScroll: (event) => {
                          const listboxNode = event.currentTarget;
                          if (
                            listboxNode.scrollTop + listboxNode.clientHeight >=
                            listboxNode.scrollHeight - 1
                          ) {
                            if (hasMoreResumes) {
                              loadMoreResumes();
                            }
                          }
                        },
                      }}
                    /> */}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-amount_resumes">
                      <FormattedMessage id="Resumes Amount" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Enter total amount of resumes"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      type="number"
                      id="customer-amount_resumes"
                      placeholder="Enter Amount Resumes"
                      {...getFieldProps("amount_resumes")}
                      error={Boolean(
                        touched.amount_resumes && errors.amount_resumes
                      )}
                      helperText={
                        touched.amount_resumes && errors.amount_resumes
                      }
                    />
                    {serverErrors.amount_resumes && (
                      <FormHelperText error>
                        {serverErrors.amount_resumes}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-status">
                      <FormattedMessage id="Status" />
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip
                          title={
                            "Set the subscription status (Default inactive)"
                          }
                        >
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <FormControl fullWidth>
                      <FormControlLabel
                        control={
                          <Switch
                            id="customer-status"
                            checked={formik.values.status}
                            {...getFieldProps("status")}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setFieldValue("status", checked);
                            }}
                          />
                        }
                        label={
                          formik.values.status ? (
                            <FormattedMessage id="Active" />
                          ) : (
                            <FormattedMessage id="Inactive" />
                          )
                        }
                      />
                    </FormControl>
                    {touched.status && errors.status && (
                      <FormHelperText error sx={{ pl: 1.75 }}>
                        {errors.status}
                      </FormHelperText>
                    )}
                    {serverErrors.status && (
                      <FormHelperText error sx={{ pl: 1.75 }}>
                        {serverErrors.status}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={6}>
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage id={customer ? "Update" : "Submit"} />
                    </Button>
                  </Grid>
                  <Grid item md={6}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={handleClose}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </LocalizationProvider>
      </FormikProvider>
    </ReusableModal>
  );
};

export default AddEditResumeSubscription;
